import { Component, Story, Knobs, Accents } from './DocTypes';

const PlaygroundKnobs: Knobs = {
  page: {
    default: 1,
    editor: 'number',
  },
  pageCount: {
    default: 10,
    editor: 'number',
  },
  accent: {
    default: 'midnightblue',
    editor: 'dropdown',
    editorOptions: Accents,
  },
  buttonCount: {
    default: '',
    editor: 'number',
  },
  showFirstLast: {
    default: true,
    editor: 'checkbox',
  },
};

const PlaygroundKnobsV3: Knobs = {
  'colour-scheme': {
    editor: 'dropdown',
    editorOptions: ['light', 'dark', 'default', ''],
    default: '',
  },
  device: {
    editor: 'dropdown',
    editorOptions: ['mobile', 'desktop', ''],
    default: '',
  },
};

const customNumberOfPagesKnob: Knobs = {
  page: {
    default: 5,
    editor: 'number',
  },
};

const PlaygroundStory: Story = {
  title: 'Playground',
  description: 'A tag-pager where you can change all the attributes',
  coreComponent: {
    html: `<tag-pager{{...knobs}}>
</tag-pager>`,
    js: `var tagPager = document.querySelector('tag-pager');
tagPager.componentOnReady().then(function() {
  tagPager.addEventListener('pageRequest', function(e) {
    tagPager.page = e.detail.page;
  });
});`,
    knobs: PlaygroundKnobs,
  },
  coreComponent_v3: {
    html: `<tag3-pager{{...knobs}}>
</tag3-pager>`,
    js: `var tagPager = document.querySelector('tag3-pager');
tagPager.addEventListener('pageRequest', function(e) {
  tagPager.page = e.detail.page;
});`,
    knobs: { ...PlaygroundKnobs, ...PlaygroundKnobsV3 },
  },
  reactWrapper: {
    jsx: `<Example />`,
    parentJSX: `Example = () => {
  const [page, setPage] = React.useState(1);
  return (
    <TagPager
      page={page} {{...knobs}}
      onPageRequest={e => setPage(e.detail.page)}/>
    )
};`,
    knobs: {
      pageCount: {
        default: 10,
        editor: 'number',
      },
      accent: {
        default: 'midnightblue',
        editor: 'dropdown',
        editorOptions: Accents,
      },
      buttonCount: {
        default: '',
        editor: 'number',
      },
      showFirstLast: {
        default: true,
        editor: 'checkbox',
      },
    },
  },
  reactWrapper_v3: {
    jsx: `<Example />`,
    parentJSX: `Example = () => {
  const [page, setPage] = React.useState(1);
  return (
    <Tag3Pager
      page={page} {{...knobs}}
      onPageRequest={e => setPage(e.detail.page)}/>
    )
};`,
    knobs: {
      pageCount: {
        default: 10,
        editor: 'number',
      },
      accent: {
        default: 'midnightblue',
        editor: 'dropdown',
        editorOptions: Accents,
      },
      buttonCount: {
        default: '',
        editor: 'number',
      },
      showFirstLast: {
        default: true,
        editor: 'checkbox',
      },
      ...PlaygroundKnobsV3,
    },
  },
};

const CustomNumerOfPagesStory: Story = {
  title: 'Custom number of pages',
  description: 'A tag-pager example displaying a custom number of pages',
  coreComponent: {
    html: `<tag-pager{{...knobs}}>
</tag-pager>`,
    js: `var tagPager = document.querySelector('tag-pager');
tagPager.componentOnReady().then(function() {
  tagPager.addEventListener('pageRequest', function(e) {
    tagPager.page = e.detail.page;
  });
});`,
    knobs: { ...PlaygroundKnobs, ...customNumberOfPagesKnob },
  },
  coreComponent_v3: {
    html: `<tag3-pager{{...knobs}}>
</tag3-pager>`,
    js: `var tagPager = document.querySelector('tag3-pager');

  tagPager.addEventListener('pageRequest', function(e) {
    tagPager.page = e.detail.page;
  });`,
    knobs: { ...PlaygroundKnobs, ...customNumberOfPagesKnob },
  },
  reactWrapper: {
    jsx: `<Example />`,
    parentJSX: `Example = () => {
    handlePageRequest = (e) => {
      document.querySelector('tag-pager').page = e.detail.page;
    };
    return (
    <TagPager{{...knobs}}
    onPageRequest={this.handlePageRequest}/>
    )
  };`,
    knobs: { ...PlaygroundKnobs, ...customNumberOfPagesKnob },
  },
  reactWrapper_v3: {
    jsx: `<Example />`,
    parentJSX: `Example = () => {
    handlePageRequest = (e) => {
      document.querySelector('tag3-pager').page = e.detail.page;
    };
    return (
    <Tag3Pager{{...knobs}}
    onPageRequest={this.handlePageRequest}/>
    )
  };`,
    knobs: { ...PlaygroundKnobs, ...customNumberOfPagesKnob },
  },
};

export const TagPagerDocs: Component = {
  type: 'component',
  name: 'pager',
  description: 'Renders buttons for paging data',
  mobileFriendly: false,
  popular: false,
  preRelease: '',
  props: [
    {
      name: 'accent',
      default: 'viridiangreen',
      description: 'The color of pager',
      type: 'Accent',
    },
    {
      name: 'page',
      default: '1',
      description: 'The current page number',
      type: 'number',
    },
    {
      name: 'pageCount',
      description:
        'The total number of pages. Note that this might not be passed if the number of pages is unknown',
      type: 'number',
    },
    {
      name: 'buttonCount',
      description: 'The number of paging buttons to display.',
      type: 'number',
      default: '5',
    },
    {
      name: 'showFirstLast',
      description: 'Whether the "go to first" and "go to last" pager buttons are visible',
      type: 'boolean',
    },
    {
      name: 'colour-scheme',
      description:
        'V3 Only: Dark mode (theme) control. Default for normal browser driven control. Dark | Light to force respective styling regardless of browser setting.',
      type: 'dark | light | default',
      default: 'light',
    },
    {
      name: 'device',
      description: 'V3 Only: Set preferred styling theme for device type.',
      type: 'desktop | mobile',
      default: 'desktop',
    },
  ],
  reactPropOverrides: [],
  events: [
    {
      name: 'pageRequest',
      description: 'Event raised when a page button is clicked',
      parameters: [
        {
          name: 'page',
          description: 'The page number',
          type: 'number',
        },
      ],
    },
  ],
  methods: [],
  stories: [PlaygroundStory, CustomNumerOfPagesStory],
};
